import "./ProjectsComponent.scss";
import {Button} from "@/components/ui/button.tsx";
import FooterComponent from "@/components/common-components/footer/FooterComponent.tsx";
import {useNavigate} from "react-router-dom";
import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {APIConfig, ENV} from "@/constants";
import {ITableColumn} from "@/models/table.model.ts";
import commonService from "@/services/common.service.ts";
import useReactQuery from "@/hooks/useReactQuery.ts";
import TableComponent from "@/components/common-components/table/TableComponent.tsx";
import {Dialog, DialogContentNoClose} from "@/components/ui/dialog";
import CreateProjectDialogComponent
    from "@/screens/app/projects-list/create-project-dialog/CreateProjectDialogComponent.tsx";
import {useDispatch, useSelector} from "react-redux";
import {resetProjectDetails, setCurrentProjectDetails} from "@/store/actions/projectdata.action.ts";
import { useIsMobile } from "@/hooks/use-mobile.ts"; // Import the mobile hook
import { Clock, Code2, FolderPlus } from "lucide-react"; // Import icons for mobile view
import {PROJECT_LAYOUT_ROUTE} from "@/constants/RoutesConfig.tsx";
import {CommonService} from "@/services";
import {useQueryClient} from "@tanstack/react-query";
import {IRootReducerState} from "@/store/reducers";

export const projectsListKey = "projects-list";

const ProjectsComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [isCreateModelOpen, setIsCreateModelOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [creationStep, setCreationStep] = useState("creating project");
    const isMobile = useIsMobile(); // Use the hook to check for mobile view
    
    // Get current prompt from Redux
    const { currentPrompt } = useSelector((state: IRootReducerState) => state.auth);
    const hasProcessedPrompt = useRef<boolean>(false);

    
    const {
        data: projectsResponse,
        isLoading: isProjectsLoading,
        isLoadingError: isProjectsLoadingFailed,
    } = useReactQuery({
        url: APIConfig.GET_USER_PROJECTS.URL,
        method: APIConfig.GET_USER_PROJECTS.METHOD as any,
        payload: {},
        options: {
            refetchOnMount: false,
            initialApiCall: true,
            staleTime: 1000 * 60 * 10, // 10 minutes
            cacheTime: 1000 * 60 * 10, // 10 minutes
        },
        queryKey: [projectsListKey],
    });

    const projects = projectsResponse || [];

    const handleViewProject = useCallback(
        (project: any) => {
            dispatch(resetProjectDetails())
            dispatch(setCurrentProjectDetails(project));
            navigate(`/project/${(project?.app_type || "react").toLowerCase()}/${project?.id}`);
        }, [dispatch, navigate]);

    const renderTimestamp = (timestamp: string) =>
        <div className="table-name">{commonService.transformTimeStamp(timestamp)}</div>;

    const tableColumns: ITableColumn[] = useMemo(
        () => [
            {
                title: "Project Name",
                key: "projectName",
                align: "left",
                fixed: "left",
                width: 300,
                render: (rowData: any) => <div className="table-name">{rowData?.name}</div>,
            },
            {
              title: "App Type",
              key:"app_type",
                align: "left",
                fixed: "left",
                width: 300,
            },
            {
                title: "Created on",
                key: "createdOn",
                align: "left",
                fixed: "left",
                render: (rowData: any) => renderTimestamp(rowData?.created_at),
            },
            {
                title: "Last Updated on",
                key: "updatedOn",
                align: "left",
                fixed: "left",
                render: (rowData: any) => renderTimestamp(rowData?.updated_at),
            },
            {
                title: "",
                key: "action",
                align: "center",
                fixed: "right",
                width: 100,
                render: (rowData: any) => (
                    <div className="table-action-buttons">
                        <Button
                            onClick={() => handleViewProject(rowData)}
                            size="sm"
                            variant="linkUnderLine"
                            color="secondary"
                            mixpanelEvent={{name: "view_project_button_clicked", properties: { project_id: rowData?.id }}}
                        >
                            View
                        </Button>
                    </div>
                ),
            },
        ],
        [handleViewProject]
    );

    const handleCreateProject = useCallback((appType: string = "nextjs") => {
        setIsCreating(true);
        setCreationStep("creating project");
        
        // Create the project directly
        CommonService._project_service.createProject({
            app_type: appType
        })
        .then((response: any) => {
            const newProject = response?.data?.project;
            queryClient.setQueryData([projectsListKey], (oldData: any) => {
                if (!oldData || oldData?.length === 0) return [newProject];
                return [newProject, ...oldData];
            });
            commonService._alert.showToast("Project created successfully", "success");
            dispatch(resetProjectDetails());
            dispatch(setCurrentProjectDetails(newProject));
            
            // For HTML projects, redirect directly to the project screen
            // For other types, go to requirement gathering
            if (appType === "html") {
                navigate(`${PROJECT_LAYOUT_ROUTE}/${appType.toLowerCase()}/${newProject?.id}`);
            } else {
                navigate(PROJECT_LAYOUT_ROUTE + '/requirement-gathering/' + newProject?.id);
            }
        })
        .catch((error: any) => {
            commonService._alert.showToast(error?.message || "Failed to create project", "error");
            setIsCreateModelOpen(false);
        })
        .finally(() => {
            setIsCreating(false);
        });
    }, [dispatch, navigate, queryClient]);

    const handleCreateHtmlProject = useCallback(() => {
        handleCreateProject("html");
    }, [handleCreateProject]);

    const handleCreateModelToggle = useCallback((isOpen: boolean, appType: string = "nextjs") => {
        if (isOpen) {
            // Start creating project when dialog opens
            handleCreateProject(appType);
        } else {
            // Reset state when dialog closes
            setIsCreating(false);
            setCreationStep("creating project");
        }
        setIsCreateModelOpen(isOpen);
    }, [handleCreateProject]);

    // Auto-create project if there's a stored prompt
    useEffect(() => {
        if (currentPrompt && !hasProcessedPrompt.current && !isProjectsLoading) {
            hasProcessedPrompt.current = true;            
            // Automatically create a project and navigate to requirement gathering
            CommonService.handleMixpanelTrack('project_auto_created_with_prompt', {
                prompt_length: currentPrompt.length
            });
            
            // Open the dialog to show creation progress
            setIsCreateModelOpen(true);
            
            handleCreateProject("nextjs");
        }
    }, [currentPrompt, isProjectsLoading, handleCreateProject]);

    // New function to render the mobile card view
    const renderMobileCard = (project: any): ReactNode => (
        <div key={project.id} className="mobile-project-card">
            <div className="card-main-info">
                <div className="project-title">{project.name || "Untitled Project"}</div>
                <Button
                    onClick={() => handleViewProject(project)}
                    size="sm"
                    variant="outline"
                    mixpanelEvent={{
                        name: "view_project_button_clicked",
                        properties: { source: "mobile_card", project_id: project.id }
                    }}
                >
                    View
                </Button>
            </div>
            <div className="card-details">
                <div className="detail-item">
                    <Code2 className="detail-icon" />
                    <span>{project.app_type || 'N/A'}</span>
                </div>
                <div className="detail-item">
                    <Clock className="detail-icon" />
                    <span>{commonService.transformTimeStamp(project.updated_at)}</span>
                </div>
            </div>
        </div>
    );
    
    return (
        <>
            <div className="projects-component w-full">
                <div className="projects-list-header mb-7">
                    <div className="projects-list-heading text-secondary">Projects</div>
                    <div className="header-actions">
                            <Button 
                                onClick={() => handleCreateModelToggle(true, "nextjs")}
                                mixpanelEvent={{
                                    name: "create_project_button_clicked",
                                    properties: { source: "projects_list_header", app_type: "nextjs" }
                                }}
                            >
                                Create Project
                            </Button>
                        {ENV.ENV_MODE === "development" && (
                            <Button 
                                onClick={handleCreateHtmlProject}
                                variant="white"
                                mixpanelEvent={{
                                    name: "create_html_project_button_clicked",
                                    properties: { source: "projects_list_header_dev", app_type: "html" }
                                }}
                            >
                                Create HTML Project (Dev)
                            </Button>
                        )}
                    </div>
                </div>

                {(!isProjectsLoading && projects.length === 0 && !isProjectsLoadingFailed) ? (
                    // Empty state
                    <div className="projects-empty-state">
                        <div className="empty-state-background-glow"></div>
                        <div className="empty-state-content">
                            <div className="empty-state-icon-wrapper">
                                <div className="icon-glow-effect"></div>
                                <FolderPlus className="empty-state-icon" />
                            </div>
                            <h2 className="empty-state-title">
                                Ready to Build Something Amazing?
                            </h2>
                            <p className="empty-state-description">
                                Transform your ideas into reality with superengineer.ai. 
                                Create stunning applications in minutes, not months.
                            </p>
                            <Button 
                                onClick={() => handleCreateModelToggle(true, "nextjs")}
                                size="lg"
                                className="empty-state-button"
                                mixpanelEvent={{name: "create_first_project_button_clicked", properties: { app_type: "nextjs" }}}
                            >
                                <FolderPlus className="button-icon" />
                                Create Your First Project
                            </Button>
                            <p className="empty-state-hint">
                                No credit card required • Free to start
                            </p>
                        </div>
                    </div>
                ) : isMobile ? (
                    // Render mobile card list
                    <div className="mobile-projects-list">
                        {isProjectsLoading ? (
                            // Mobile loading skeleton
                            <div className="mobile-projects-loading">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="mobile-project-card skeleton">
                                        <div className="card-main-info">
                                            <div className="skeleton-title"></div>
                                            <div className="skeleton-button"></div>
                                        </div>
                                        <div className="card-details">
                                            <div className="skeleton-detail"></div>
                                            <div className="skeleton-detail"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : isProjectsLoadingFailed ? (
                            <div className="mobile-error-state">
                                <p>Failed to load projects</p>
                            </div>
                        ) : (
                            projects.map(renderMobileCard)
                        )}
                    </div>
                ) : (
                    // Render desktop table
                    <TableComponent
                        data={projects}
                        columns={tableColumns}
                        loading={isProjectsLoading}
                        errored={isProjectsLoadingFailed}
                        noDataText="No Projects Found"
                        errorText="Failed to load projects"
                    />
                )}
                <FooterComponent/>
            </div>

            <Dialog open={isCreateModelOpen} onOpenChange={()=>{}} >
                <DialogContentNoClose className="ps-0 create-project-dialog-content animated-border-container">
                    <CreateProjectDialogComponent 
                        isCreating={isCreating}
                        creationStep={creationStep}
                    />
                </DialogContentNoClose>
            </Dialog>
        </>
    );
};

export default ProjectsComponent;